import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  Typography,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";

import { clearToken } from "./authUtils";

const DRAWER_WIDTH = 240;

const navItems = [
  { label: "Dashboard", to: "/admin", icon: <DashboardRoundedIcon />, end: true },
  { label: "Construction Update", to: "/admin/construction", icon: <ConstructionRoundedIcon /> },
  { label: "Videos", to: "/admin/videos", icon: <VideocamRoundedIcon /> },
  { label: "Payment Plans", to: "/admin/payment-plans", icon: <PaymentsRoundedIcon /> },
  { label: "Documents", to: "/admin/documents", icon: <DescriptionRoundedIcon /> },
  { label: "Gallery", to: "/admin/gallery", icon: <CollectionsRoundedIcon /> },
  { label: "Settings", to: "/admin/settings", icon: <SettingsRoundedIcon /> },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const navList = (
    <List sx={{ mt: 1 }}>
      {navItems.map((item) => (
        <ListItemButton
          key={item.to}
          component={NavLink}
          to={item.to}
          end={item.end}
          onClick={() => setMobileOpen(false)}
          sx={{
            "&.active": {
              bgcolor: "action.selected",
              borderRight: "3px solid",
              borderColor: "primary.main",
            },
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: "#111827", color: "#fff" }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileOpen(true)}
                title="Menu"
              >
                <MenuRoundedIcon />
              </IconButton>
            )}
            <Typography variant="h6" noWrap fontWeight={700} sx={{ color: "#fff" }}>
              HB Heights — Admin
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={handleLogout} title="Logout" sx={{ color: "#fff" }}>
            <LogoutRoundedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: "border-box" },
          }}
        >
          <Toolbar />
          {navList}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          bgcolor: "#f4f5f7",
          minHeight: "100vh",
          width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` },
          overflowX: "hidden",
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
